import React from 'react';
import cx from 'classnames';
import px from 'prop-types';
import * as PRODUCT from '~config/product';
import { Formatter } from 'Common/utils';
import { Spinner } from 'Common/components/ui';
import { StockStatus } from 'Common/constants/product';
import { useTranslation } from 'Common/hooks';
import { Translation } from 'Common/components/localization';
import { LocallyWidget } from 'Common/components/locator';
import ProductInput from './ProductInput';

const EMPTY_VALUE = '—';

function priceProps(listPrice, salePrice) {
    const retailStr = listPrice ? Formatter.currency(listPrice) : '';
    const saleStr = salePrice && salePrice < listPrice ? Formatter.currency(salePrice) : '';

    return { price: retailStr, reducedPrice: saleStr };
}

function normalizeString(value) {
    if (value == null) {
        return '';
    }

    return String(value).trim();
}

function normalizeJoinedValue(values) {
    if (!Array.isArray(values) || !values.length) {
        return '';
    }

    return values
        .map((value) => normalizeString(value))
        .filter(Boolean)
        .join(', ');
}

function arrayToAttributeMap(attributes = []) {
    return attributes.reduce((accumulator, attribute) => {
        if (!attribute?.AttributeName) {
            return accumulator;
        }

        accumulator[attribute.AttributeName] = attribute;
        return accumulator;
    }, {});
}

function getMatchedChild(item) {
    return item?.CatalogEntity?.Children?.find((child) => child.Code === item.Code) || null;
}

function normalizeLineItemAttributes(attributes = {}) {
    return Object.keys(attributes || {}).reduce((accumulator, attributeName) => {
        const attribute = attributes[attributeName];

        if (!attributeName) {
            return accumulator;
        }

        accumulator[attributeName] = {
            AttributeName: attributeName,
            AttributeDisplayName: attribute?.AttributeDisplayName || attributeName,
            Values: Array.isArray(attribute?.Values) ? attribute.Values : [],
            IsSpecification: Boolean(attribute?.IsSpecification),
            Order: Number.isFinite(attribute?.Order) ? attribute.Order : 9999,
        };

        return accumulator;
    }, {});
}

function getAttributeDisplayName(attribute, fallbackName) {
    return attribute?.AttributeDisplayName || fallbackName;
}

function isUsefulAttributeName(attributeName) {
    if (!attributeName) {
        return false;
    }

    const lowerName = attributeName.toLowerCase();

    return ![
        'sap',
        'dck',
        'bps',
        'planogram',
        'warrantyverbiage',
        'proposition',
        'prop65',
        'status1100',
        'catalogstatus',
        'countryoforigin',
        'tariff',
        'gtin',
        'number',
        'upp',
        'dom',
    ].some((ignoredFragment) => lowerName.includes(ignoredFragment));
}

function isCandidateSpec(attribute) {
    if (!attribute?.AttributeName) {
        return false;
    }

    if (attribute.IsSpecification) {
        return true;
    }

    return isUsefulAttributeName(attribute.AttributeName);
}

function normalizeConfiguredSections(compareSections) {
    if (!Array.isArray(compareSections)) {
        return [];
    }

    return compareSections
        .map((section, index) => ({
            key: `configured-section-${index}`,
            title: section?.title || '',
            description: section?.description || '',
            attributeNames: Array.isArray(section?.attributeNames) ? section.attributeNames.filter(Boolean) : [],
        }))
        .filter((section) => section.title && section.attributeNames.length);
}

function buildConfiguredSections(rows, compareSections, unassignedSectionText) {
    const normalizedSections = normalizeConfiguredSections(compareSections);
    const assignedAttributeNames = new Set();

    const sections = normalizedSections.map((section) => {
        const sectionRows = section.attributeNames
            .map((attributeName) => rows.find((row) => row.attributeName === attributeName))
            .filter(Boolean);

        section.attributeNames.forEach((attributeName) => {
            assignedAttributeNames.add(attributeName);
        });

        return {
            key: section.key,
            title: section.title,
            description: section.description,
            rows: sectionRows,
        };
    });

    const unassignedRows = rows.filter((row) => !assignedAttributeNames.has(row.attributeName));

    if (unassignedRows.length) {
        sections.push({
            key: 'additional-unassigned',
            title: unassignedSectionText.title,
            description: unassignedSectionText.description,
            rows: unassignedRows,
        });
    }

    return sections;
}

function buildComparableAttributes(item) {
    const matchedChild = getMatchedChild(item);
    const lineItemAttributes = normalizeLineItemAttributes(item?.Attributes);
    const childAttributes = arrayToAttributeMap(matchedChild?.Attributes);
    const productAttributes = arrayToAttributeMap(item?.CatalogEntity?.Attributes);

    return {
        lineItemAttributes,
        childAttributes,
        productAttributes,
    };
}

function getNormalizedAttributeValue(normalizedItem, attributeName) {
    const lineItemAttribute = normalizedItem.lineItemAttributes?.[attributeName];
    const childAttribute = normalizedItem.childAttributes?.[attributeName];
    const productAttribute = normalizedItem.productAttributes?.[attributeName];

    return (
        normalizeJoinedValue(lineItemAttribute?.Values) ||
        normalizeJoinedValue(childAttribute?.Values) ||
        normalizeJoinedValue(productAttribute?.Values) ||
        ''
    );
}

function getItemTitle(item) {
    return item?.Title || item?.DisplayName || item?.CatalogEntity?.DisplayName || item?.Code || '';
}

function getItemUrl(item) {
    return item?.Url || item?.ProductUrl || item?.CatalogEntity?.ContentUrl || '#';
}

function getItemImageUrl(item) {
    return item?.ImageUrl || item?.CatalogEntity?.DefaultImageUrl || null;
}

function buildNormalizedItem(item) {
    const comparableAttributes = buildComparableAttributes(item);

    return {
        raw: item,
        code: item?.Code,
        productCode: item?.ProductCode || item?.CatalogEntity?.Code,
        title: getItemTitle(item),
        url: getItemUrl(item),
        imageUrl: getItemImageUrl(item),
        stockStatus: item?.StockStatus || item?.CatalogEntity?.StockStatus,
        listPrice: item?.ListPrice,
        salePrice: item?.SalePrice,
        ...comparableAttributes,
    };
}

function collectObservedRowDefinitions(normalizedItems, specs) {
    const rowMap = new Map();

    (specs || []).forEach((spec, index) => {
        if (!spec?.AttributeName) {
            return;
        }

        rowMap.set(spec.AttributeName, {
            attributeName: spec.AttributeName,
            label: spec.AttributeDisplayName || spec.AttributeName,
            order: Number.isFinite(spec?.Order) ? spec.Order : index,
            fromSpecDefinitions: true,
        });
    });

    normalizedItems.forEach((item) => {
        [item.lineItemAttributes, item.childAttributes, item.productAttributes].forEach((attributeMap) => {
            Object.keys(attributeMap || {}).forEach((attributeName) => {
                const attribute = attributeMap[attributeName];

                if (!isCandidateSpec(attribute)) {
                    return;
                }

                if (!rowMap.has(attributeName)) {
                    rowMap.set(attributeName, {
                        attributeName,
                        label: getAttributeDisplayName(attribute, attributeName),
                        order: Number.isFinite(attribute?.Order) ? attribute.Order : 9999,
                        fromSpecDefinitions: false,
                    });
                }
            });
        });
    });

    return Array.from(rowMap.values()).sort((left, right) => {
        if (left.order !== right.order) {
            return left.order - right.order;
        }

        return left.label.localeCompare(right.label);
    });
}

function buildRowData(normalizedItems, rowDefinitions) {
    return rowDefinitions.map((rowDefinition) => {
        const values = normalizedItems.map((item) => getNormalizedAttributeValue(item, rowDefinition.attributeName));
        const normalizedValues = values.map((value) => value || EMPTY_VALUE);
        const populatedCount = values.filter(Boolean).length;
        const distinctValues = new Set(normalizedValues);
        const different = distinctValues.size > 1;

        return {
            ...rowDefinition,
            values: normalizedValues,
            populatedCount,
            different,
        };
    });
}

function filterRows(rows, { showDifferencesOnly, showCommonOnly, comparedCount }) {
    return rows.filter((row) => {
        if (showDifferencesOnly && !row.different) {
            return false;
        }

        if (showCommonOnly && row.populatedCount !== comparedCount) {
            return false;
        }

        return true;
    });
}

function getComparedCatalogEntity(comparedItems, code) {
    return comparedItems.find((item) => item.Code === code)?.CatalogEntity || null;
}

function EmptyValue() {
    return <span className="ProductCompare__empty-value">{EMPTY_VALUE}</span>;
}

function ComparisonMatrix({ items, rows, title, description, className }) {
    if (!rows.length) {
        return (
            <section className={cx('ProductCompare__matrix-section', className)}>
                <div className="ProductCompare__section-header">
                    <h2 className="ProductCompare__section-title">{title}</h2>
                    {description ? <p className="ProductCompare__section-copy">{description}</p> : null}
                </div>
                <div className="ProductCompare__empty-panel">No matching attributes to display for this section.</div>
            </section>
        );
    }

    return (
        <section className={cx('ProductCompare__matrix-section', className)}>
            <div className="ProductCompare__section-header">
                <h2 className="ProductCompare__section-title">{title}</h2>
                {description ? <p className="ProductCompare__section-copy">{description}</p> : null}
            </div>

            <div className="ProductCompare__matrix" style={{ '--product-compare-columns': items.length }}>
                <div className="ProductCompare__matrix-row ProductCompare__matrix-row--header">
                    <div className="ProductCompare__matrix-label ProductCompare__matrix-label--header">Attribute</div>
                    {items.map((item) => (
                        <div
                            key={`header-${item.code}`}
                            className="ProductCompare__matrix-cell ProductCompare__matrix-cell--header"
                        >
                            {item.title}
                        </div>
                    ))}
                </div>

                {rows.map((row) => (
                    <div
                        key={row.attributeName}
                        className={cx('ProductCompare__matrix-row', {
                            'ProductCompare__matrix-row--different': row.different,
                        })}
                    >
                        <div className="ProductCompare__matrix-label">{row.label}</div>

                        {row.values.map((value, index) => {
                            const item = items[index];

                            return (
                                <div key={`${row.attributeName}-${item?.code}`} className="ProductCompare__matrix-cell">
                                    <span className="ProductCompare__matrix-cell-mobile-code">{item?.code}</span>
                                    <div className="ProductCompare__matrix-cell-value">
                                        {value === EMPTY_VALUE ? <EmptyValue /> : value}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </section>
    );
}

ComparisonMatrix.propTypes = {
    items: px.arrayOf(
        px.shape({
            code: px.string.isRequired,
            title: px.string.isRequired,
        })
    ).isRequired,
    rows: px.arrayOf(
        px.shape({
            attributeName: px.string.isRequired,
            label: px.string.isRequired,
            values: px.arrayOf(px.string).isRequired,
            different: px.bool.isRequired,
        })
    ).isRequired,
    title: px.string.isRequired,
    description: px.string,
    className: px.string,
};

ComparisonMatrix.defaultProps = {
    description: undefined,
    className: undefined,
};

export default function ProductCompare({
    items,
    specs,
    onRemoveFromCompareList,
    onClearCompareList,
    onAddItemToCart,
    isLoading,
    gtmListValue,
    comparedItems,
    compareSections,
    locallyConfig,
    className,
}) {
    const [showDifferencesOnly, setShowDifferencesOnly] = React.useState(false);
    const [showCommonOnly, setShowCommonOnly] = React.useState(false);

    const unassignedSectionTitle = useTranslation('Commerce.Product.Compare.AdditionalDetails.Title');

    const unassignedSectionDescription = useTranslation('Commerce.Product.Compare.AdditionalDetails.Description');

    const normalizedItems = React.useMemo(() => (items || []).map(buildNormalizedItem), [items]);

    const rowDefinitions = React.useMemo(
        () => collectObservedRowDefinitions(normalizedItems, specs),
        [normalizedItems, specs]
    );

    const allRows = React.useMemo(
        () => buildRowData(normalizedItems, rowDefinitions),
        [normalizedItems, rowDefinitions]
    );

    const visibleRows = React.useMemo(
        () =>
            filterRows(allRows, {
                showDifferencesOnly,
                showCommonOnly,
                comparedCount: normalizedItems.length,
            }),
        [allRows, normalizedItems.length, showDifferencesOnly, showCommonOnly]
    );

    const configuredSections = React.useMemo(
        () =>
            buildConfiguredSections(visibleRows, compareSections, {
                title: unassignedSectionTitle,
                description: unassignedSectionDescription,
            }),
        [visibleRows, compareSections, unassignedSectionTitle, unassignedSectionDescription]
    );

    const onClickRemove = React.useCallback(
        (code) => (event) => {
            event.preventDefault();
            onRemoveFromCompareList(code);
        },
        [onRemoveFromCompareList]
    );

    const onClickClear = React.useCallback(() => {
        onClearCompareList();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [onClearCompareList]);

    const onToggleDifferencesOnly = React.useCallback(() => {
        setShowDifferencesOnly((current) => !current);
    }, []);

    const onToggleCommonOnly = React.useCallback(() => {
        setShowCommonOnly((current) => !current);
    }, []);

    const productInfo = React.useCallback((code) => getComparedCatalogEntity(comparedItems, code), [comparedItems]);

    if (isLoading) {
        return (
            <div className={cx('ProductCompare ProductCompare--loading', className)}>
                <Spinner />
            </div>
        );
    }

    if (!items?.length) {
        return (
            <section className={cx('ProductCompare ProductCompare--empty', className)}>
                <div className="ProductCompare__empty-state">
                    <h1 className="ProductCompare__empty-title">
                        <Translation id="Commerce.Product.Compare.NoItems" />
                    </h1>
                    <p className="ProductCompare__empty-copy">
                        <Translation id="Commerce.Product.Compare.NoItems.Copy" />
                    </p>
                </div>
            </section>
        );
    }

    return (
        <section className={cx('ProductCompare', className)} aria-label="Product comparison">
            <header className="ProductCompare__hero">
                <div className="ProductCompare__hero-copy">
                    <h1 className="ProductCompare__title">
                        <Translation id="Commerce.Product.Compare.SectionHeader" />
                    </h1>
                    <p className="ProductCompare__subtitle">
                        <Translation id="Commerce.Product.Compare.SectionSubtitle" />
                    </p>
                </div>

                <div className="ProductCompare__toolbar">
                    <label className="ProductCompare__toggle">
                        <input type="checkbox" checked={showDifferencesOnly} onChange={onToggleDifferencesOnly} />
                        <span>
                            <Translation id="Commerce.Product.Compare.Toggle.Differences" />
                        </span>
                    </label>

                    <label className="ProductCompare__toggle">
                        <input type="checkbox" checked={showCommonOnly} onChange={onToggleCommonOnly} />
                        <span>
                            <Translation id="Commerce.Product.Compare.Toggle.CommonSpecs" />
                        </span>
                    </label>

                    <button type="button" className="btn btn-secondary ProductCompare__clear" onClick={onClickClear}>
                        <Translation id="Commerce.Product.Compare.Clear" />
                    </button>
                </div>
            </header>

            <div className="ProductCompare__cards" role="list" aria-label="Compared products">
                {normalizedItems.map((item) => {
                    const { price, reducedPrice } = priceProps(item.listPrice, item.salePrice);
                    const isDisplayOnly = item.stockStatus === StockStatus.DisplayOnly;
                    const isInStock = item.stockStatus === StockStatus.InStock || item.stockStatus === 'InStock';
                    const brand =
                        getNormalizedAttributeValue(item, 'ProductBrand') ||
                        getNormalizedAttributeValue(item, 'ItemBrand');

                    const family =
                        getNormalizedAttributeValue(item, 'ProductFamily') ||
                        getNormalizedAttributeValue(item, 'ItemFamily');

                    const category =
                        getNormalizedAttributeValue(item, 'ProductCategory') ||
                        getNormalizedAttributeValue(item, 'ItemCategory');

                    const summaryChips = [brand, family, category].filter(Boolean);

                    return (
                        <article key={item.code} className="ProductCompare__card" role="listitem">
                            <button
                                type="button"
                                className="ProductCompare__remove"
                                onClick={onClickRemove(item.code)}
                                aria-label={`Remove ${item.title} from compare`}
                            >
                                <i className="fa fa-times" aria-hidden="true"></i>
                            </button>

                            <a className="ProductCompare__card-link" href={item.url}>
                                <div className="ProductCompare__image-wrap">
                                    {item.imageUrl ? (
                                        <img
                                            className="ProductCompare__image"
                                            src={`${item.imageUrl}?format=png&height=450&width=550${
                                                PRODUCT.TRANSFORM_PRODUCT_IMAGE_BACKGROUND ? '&transBg=true' : ''
                                            }`}
                                            alt={item.title}
                                        />
                                    ) : (
                                        <div className="ProductCompare__image ProductCompare__image--placeholder" />
                                    )}
                                </div>

                                <div className="ProductCompare__card-body">
                                    <div className="ProductCompare__chips" aria-hidden="true">
                                        {summaryChips.map((chip) => (
                                            <span key={`${item.code}-${chip}`} className="ProductCompare__chip">
                                                {chip}
                                            </span>
                                        ))}
                                    </div>

                                    <h2 className="ProductCompare__card-title">{item.title}</h2>

                                    <div className="ProductCompare__price-block">
                                        <div className={cx('ProductCompare__price', { strike: reducedPrice })}>
                                            {price}
                                        </div>
                                        {reducedPrice ? (
                                            <div className="ProductCompare__reduced-price">{reducedPrice}</div>
                                        ) : null}
                                    </div>
                                </div>
                            </a>

                            <div className="ProductCompare__meta-row">
                                <div className="ProductCompare__meta-item">
                                    <span className="ProductCompare__meta-label">
                                        <Translation id="Commerce.Product.Tabs.Sku.Label" />
                                    </span>
                                    <span className="ProductCompare__meta-value">{item.code}</span>
                                </div>

                                {isDisplayOnly ? null : (
                                    <div className="ProductCompare__meta-item">
                                        <span className="ProductCompare__meta-label">
                                            <Translation id="Commerce.Product.Compare.Availability" />
                                        </span>
                                        <span
                                            className={cx('ProductCompare__stock-badge', {
                                                'ProductCompare__stock-badge--in-stock': isInStock,
                                                'ProductCompare__stock-badge--out-of-stock': !isInStock,
                                            })}
                                        >
                                            {isInStock ? 'In stock' : 'Out of stock'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="ProductCompare__card-actions">
                                {isDisplayOnly ? (
                                    locallyConfig ? (
                                        <LocallyWidget
                                            className="ProductCompare__find-locally"
                                            productCode={item.productCode}
                                            productUrl={item.url}
                                            companyName={locallyConfig.companyName}
                                            companyId={locallyConfig.companyId}
                                            endpointUrl={locallyConfig.endpointUrl}
                                        />
                                    ) : (
                                        <a
                                            className="btn btn-secondary ProductCompare__find-locally"
                                            href={item.url}
                                        >
                                            <i className="fa fa-map-marker-alt me-2" aria-hidden="true"></i>
                                            <Translation
                                                id="Commerce.Product.Compare.FindItLocally"
                                                notFoundValue="Find it Locally"
                                            />
                                        </a>
                                    )
                                ) : isInStock ? (
                                    <ProductInput
                                        currentChildren={[{}]}
                                        currentItemStatus={{
                                            hasStock: isInStock,
                                            code: item.code,
                                        }}
                                        disabled={isLoading || !isInStock}
                                        gtmListValue={gtmListValue}
                                        onAddToCart={onAddItemToCart}
                                        productInfo={productInfo(item.code)}
                                        statusTooltip="top"
                                        quantity={1}
                                        itemCodes={[item.code]}
                                    />
                                ) : (
                                    <button type="button" className="btn msg-outOfStock disabled" disabled>
                                        <Translation id="Commerce.Product.ProductOutOfStock" />
                                    </button>
                                )}
                            </div>
                        </article>
                    );
                })}
            </div>

            {configuredSections.map((section) => (
                <ComparisonMatrix
                    key={section.key}
                    items={normalizedItems}
                    rows={section.rows}
                    title={section.title}
                    description={section.description}
                    className="ProductCompare__section"
                />
            ))}
        </section>
    );
}

ProductCompare.propTypes = {
    items: px.arrayOf(
        px.shape({
            Code: px.string.isRequired,
            DisplayName: px.string,
            Title: px.string,
            Url: px.string,
            ProductUrl: px.string,
            ImageUrl: px.string,
            ListPrice: px.number,
            SalePrice: px.number,
            StockStatus: px.string,
            Attributes: px.object,
            CatalogEntity: px.shape({
                DisplayName: px.string,
                ContentUrl: px.string,
                DefaultImageUrl: px.string,
                StockStatus: px.string,
                Attributes: px.arrayOf(
                    px.shape({
                        AttributeName: px.string,
                        AttributeDisplayName: px.string,
                        Values: px.arrayOf(px.any),
                        IsSpecification: px.bool,
                        Order: px.number,
                    })
                ),
                Children: px.arrayOf(
                    px.shape({
                        Code: px.string,
                        Attributes: px.arrayOf(
                            px.shape({
                                AttributeName: px.string,
                                AttributeDisplayName: px.string,
                                Values: px.arrayOf(px.any),
                                IsSpecification: px.bool,
                                Order: px.number,
                            })
                        ),
                    })
                ),
            }),
        })
    ),
    specs: px.arrayOf(
        px.shape({
            AttributeName: px.string.isRequired,
            AttributeDisplayName: px.string,
            Order: px.number,
        })
    ),
    onRemoveFromCompareList: px.func.isRequired,
    onClearCompareList: px.func.isRequired,
    onAddItemToCart: px.func.isRequired,
    isLoading: px.bool,
    gtmListValue: px.string,
    comparedItems: px.arrayOf(px.object),
    compareSections: px.arrayOf(
        px.shape({
            title: px.string,
            description: px.string,
            attributeNames: px.arrayOf(px.string),
        })
    ),
    locallyConfig: px.shape({
        companyName: px.string,
        companyId: px.string,
        endpointUrl: px.string,
    }),
    className: px.string,
};

ProductCompare.defaultProps = {
    items: [],
    specs: [],
    isLoading: false,
    gtmListValue: undefined,
    comparedItems: [],
    compareSections: [],
    locallyConfig: null,
    className: undefined,
};
